import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import LiveMonitor from "@/pages/LiveMonitor";
import Employees from "@/pages/Employees";
import Processes from "@/pages/Processes";
import Policies from "@/pages/Policies";
import Attendance from "@/pages/Attendance";
import Reports from "@/pages/Reports";
import AuditLogs from "@/pages/AuditLogs";
import Notifications from "@/pages/Notifications";
import Corrections from "@/pages/Corrections";
import Exceptions from "@/pages/Exceptions";
import Profile from "@/pages/Profile";

function Loader() {
  return <div className="flex min-h-screen items-center justify-center bg-[#F4F7FA] text-slate-400">Loading…</div>;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

const MGR = ["manager", "senior_manager", "team_lead"];
const STAFF = ["admin", ...MGR];

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<RootRedirect />} />

      <Route path="/admin" element={<Protected roles={["admin"]}><Dashboard role="admin" /></Protected>} />
      <Route path="/manager" element={<Protected roles={MGR}><Dashboard role="manager" /></Protected>} />
      <Route path="/me" element={<Protected roles={["employee"]}><EmployeeDashboard /></Protected>} />

      <Route path="/monitor" element={<Protected roles={STAFF}><LiveMonitor /></Protected>} />
      <Route path="/employees" element={<Protected roles={STAFF}><Employees /></Protected>} />
      <Route path="/processes" element={<Protected roles={["admin"]}><Processes /></Protected>} />
      <Route path="/policies" element={<Protected roles={["admin"]}><Policies /></Protected>} />
      <Route path="/attendance" element={<Protected roles={STAFF}><Attendance /></Protected>} />
      <Route path="/reports" element={<Protected roles={STAFF}><Reports /></Protected>} />
      <Route path="/exceptions" element={<Protected roles={STAFF}><Exceptions /></Protected>} />
      <Route path="/audit" element={<Protected roles={["admin"]}><AuditLogs /></Protected>} />
      <Route path="/notifications" element={<Protected roles={["admin", ...MGR, "employee"]}><Notifications /></Protected>} />
      <Route path="/corrections" element={<Protected roles={["admin", ...MGR, "employee"]}><Corrections /></Protected>} />

      <Route path="/my-attendance" element={<Protected roles={["employee"]}><Attendance selfOnly /></Protected>} />
      <Route path="/my-breaks" element={<Protected roles={["employee"]}><Reports /></Protected>} />
      <Route path="/profile" element={<Protected roles={["employee"]}><Profile /></Protected>} />

      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
